/**
 * Field categorization for the order-update engine (PATCH /orders/:id and the ingest
 * update path). Every editable-surface field maps to exactly one category; anything not
 * explicitly listed is treated as NON_EDITABLE (fail closed). Shared with the frontend —
 * keep this module free of backend-only imports.
 */

export const ORDER_UPDATE_FIELD_CATEGORY = {
  NON_EDITABLE: 'non_editable',
  NON_IMPACTING: 'non_impacting',
  PLANNING_IMPACTING: 'planning_impacting',
} as const;
export type OrderUpdateFieldCategory = (typeof ORDER_UPDATE_FIELD_CATEGORY)[keyof typeof ORDER_UPDATE_FIELD_CATEGORY];

/** Field paths (dot notation) touched by a patch, grouped by category. */
export type CategorizedOrderUpdate = Record<OrderUpdateFieldCategory, string[]>;

// ─── Categorization maps ────────────────────────────────────────────────────────

const TOP_LEVEL_FIELD_CATEGORY: Partial<Record<string, OrderUpdateFieldCategory>> = {
  handlingUnits: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  instruction: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  orderValue: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  requestedCarrierCode: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  requestedTimeWindow: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  tags: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  totalHandlingUnitQuantity: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  totalVolume: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  totalWeight: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
};

const ADDRESS_SUBFIELD_CATEGORY: Record<string, OrderUpdateFieldCategory> = {
  additionalInformations: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  city: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  complement: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  coordinates: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  country: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  digicode: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  floor: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  full: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  hasElevator: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  houseNumber: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  postalCode: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  street: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
};

const CONTACT_SUBFIELD_CATEGORY: Record<string, OrderUpdateFieldCategory> = {
  name: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  phone: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
};

// The operator-facing PATCH /orders/:id surface only exposes email/phone (see
// UpdateOrderByIdBody); firstName/lastName/id are additionally accepted here so the ingest
// path — whose nested customer DTO always requires firstName/lastName/phone together, per
// CreateOrderBody — keeps behaving exactly as before (see updateByExternalId).
const CUSTOMER_SUBFIELD_CATEGORY: Record<string, OrderUpdateFieldCategory> = {
  email: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  firstName: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  id: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  lastName: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  phone: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
};

// ─── Public API ─────────────────────────────────────────────────────────────────

interface CategorizedField {
  category: OrderUpdateFieldCategory;
  path: string;
}

/**
 * Categorizes every field present in an order-update patch. Works against both the
 * narrow operator-facing patch (UpdateOrderByIdBody) and the broader ingest patch
 * (Partial<Order>-shaped) — only field names are inspected, so extra properties on
 * either shape are safely ignored.
 */
export function categorizeOrderUpdate<T extends object>(patch: T): CategorizedOrderUpdate {
  const result: CategorizedOrderUpdate = {
    [ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE]: [],
    [ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING]: [],
    [ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING]: [],
  };

  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    for (const field of resolveTopLevelField(key, value)) {
      result[field.category].push(field.path);
    }
  }

  return result;
}

// ─── Helpers ────────────────────────────────────────────────────────────────────

function resolveTopLevelField(key: string, value: unknown): CategorizedField[] {
  if (key === 'customer') {
    return resolveNestedFields('customer', value, CUSTOMER_SUBFIELD_CATEGORY);
  }
  if (key === 'delivery') {
    return resolveDeliveryFields(value);
  }
  if (key === 'pickup') {
    return resolvePickupFields(value);
  }
  return [{ category: TOP_LEVEL_FIELD_CATEGORY[key] ?? ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE, path: key }];
}

function resolveDeliveryFields(value: unknown): CategorizedField[] {
  if (!isPlainObject(value)) {
    return [{ category: ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE, path: 'delivery' }];
  }

  const fields: CategorizedField[] = [];
  for (const [subKey, subValue] of Object.entries(value)) {
    if (subValue === undefined) continue;
    if (subKey === 'address') {
      fields.push(...resolveNestedFields('delivery.address', subValue, ADDRESS_SUBFIELD_CATEGORY));
    } else if (subKey === 'contact') {
      fields.push(...resolveContactField('delivery.contact', subValue));
    } else if (subKey === 'timeSlot') {
      // Direct leg edit; fails closed to NON_EDITABLE on a non-plain-object value (e.g. null).
      fields.push({
        category: isPlainObject(subValue)
          ? ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING
          : ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE,
        path: 'delivery.timeSlot',
      });
    } else if (subKey === 'durationTime') {
      // Direct leg edit; fails closed to NON_EDITABLE on a non-numeric value.
      fields.push({
        category:
          typeof subValue === 'number'
            ? ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING
            : ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE,
        path: 'delivery.durationTime',
      });
    } else {
      fields.push({ category: ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE, path: `delivery.${subKey}` });
    }
  }
  return fields;
}

function resolvePickupFields(value: unknown): CategorizedField[] {
  if (!isPlainObject(value)) {
    return [{ category: ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE, path: 'pickup' }];
  }

  const fields: CategorizedField[] = [];
  for (const [subKey, subValue] of Object.entries(value)) {
    if (subValue === undefined) continue;
    if (subKey === 'contact') {
      fields.push(...resolveContactField('pickup.contact', subValue));
    } else if (subKey === 'timeSlot') {
      // Direct leg edit; fails closed to NON_EDITABLE on a non-plain-object value (e.g. null).
      fields.push({
        category: isPlainObject(subValue)
          ? ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING
          : ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE,
        path: 'pickup.timeSlot',
      });
    } else if (subKey === 'durationTime') {
      // Direct leg edit; fails closed to NON_EDITABLE on a non-numeric value.
      fields.push({
        category:
          typeof subValue === 'number'
            ? ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING
            : ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE,
        path: 'pickup.durationTime',
      });
    } else {
      fields.push({ category: ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE, path: `pickup.${subKey}` });
    }
  }
  return fields;
}

/** An explicit `null` contact (removal) categorizes as NON_IMPACTING; a real object goes per-subfield. */
function resolveContactField(prefix: string, value: unknown): CategorizedField[] {
  if (value === null) {
    return [{ category: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING, path: prefix }];
  }
  return resolveNestedFields(prefix, value, CONTACT_SUBFIELD_CATEGORY);
}

function resolveNestedFields(
  prefix: string,
  value: unknown,
  categoryBySubfield: Record<string, OrderUpdateFieldCategory>,
): CategorizedField[] {
  if (!isPlainObject(value)) {
    return [{ category: ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE, path: prefix }];
  }

  return Object.entries(value)
    .filter(([, subValue]) => subValue !== undefined)
    .map(([subKey]) => ({
      category: categoryBySubfield[subKey] ?? ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE,
      path: `${prefix}.${subKey}`,
    }));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
